// Asset360MockData.js

export const entities = ['Asset', 'AssetCategory', 'Maintenance'];

export const mockData = {
  Asset: {
    attributes: [
      {
        name: 'AssetId',
        type: 'String',
        description: 'Unique identifier for each asset in the organization.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'AssetName',
        type: 'String',
        description: 'The given name or label of the asset.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'ModelNumber',
        type: 'String',
        description: 'Manufacturer model or serial number of the asset.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'PurchaseDate',
        type: 'Date',
        description: 'The date when the asset was purchased or acquired.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'Status',
        type: 'String',
        description: 'The operational state of the asset (Active, Retired, In Repair).',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
    ],

    relationships: [
      { from: 'Asset', relationship: 'belongsTo', to: 'AssetCategory' },
      { from: 'Asset', relationship: 'hasMany', to: 'Maintenance' },
    ],

    survivorship: [
      {
        id: 1,
        attribute: 'AssetId',
        strategy: 'Primary Key (Unique Identifier)',
        sourcePriority: '—',
        fallback: 'No Fallback (System Generated)',
      },
      {
        id: 2,
        attribute: 'AssetName',
        strategy: 'Preferred Source',
        sourcePriority: 'ERP > CMMS > Inventory',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 3,
        attribute: 'ModelNumber',
        strategy: 'Most Frequent Value',
        sourcePriority: 'ERP > Inventory',
        fallback: 'Non-null',
      },
      {
        id: 4,
        attribute: 'PurchaseDate',
        strategy: 'Earliest Valid Date',
        sourcePriority: 'ERP > Procurement',
        fallback: 'Non-null',
      },
      {
        id: 5,
        attribute: 'Status',
        strategy: 'Most Recent Update',
        sourcePriority: 'ERP > CMMS',
        fallback: 'Active by Default',
      },
    ],

    matchRules: [
      { label: 'A03: Exact Name, Address', name: 'Rule5', type: 'Potential Match', scope: 'All' },
      { label: 'A04: Exact Name, Email', name: 'Rule6', type: 'Potential Match', scope: 'All' },
      {
        label: 'Automatic match by Asset Serial Number',
        name: 'ExactMatchBySerial',
        type: 'Auto Merge',
        scope: 'All',
      },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Trim Asset Name',
        appliesTo: 'AssetName',
        providerType: 'String Normalization',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Validate Purchase Date',
        appliesTo: 'PurchaseDate',
        providerType: 'Date Validation',
        order: 2,
        status: 'Active',
      },
      {
        id: 3,
        ruleName: 'Normalize Model Number',
        appliesTo: 'ModelNumber',
        providerType: 'Uppercase Format',
        order: 3,
        status: 'Limited',
      },
    ],
  },

  AssetCategory: {
    attributes: [
      {
        name: 'CategoryId',
        type: 'String',
        description: 'Unique identifier for the asset category.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'CategoryName',
        type: 'String',
        description: 'The name of the asset category (e.g., Vehicles, Equipment).',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Description',
        type: 'String',
        description: 'Detailed explanation of the asset category purpose.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
    ],

    relationships: [{ from: 'AssetCategory', relationship: 'hasMany', to: 'Asset' }],

    survivorship: [
      {
        id: 1,
        attribute: 'CategoryId',
        strategy: 'Primary Key (Unique Identifier)',
        sourcePriority: '—',
        fallback: 'No Fallback (System Generated)',
      },
      {
        id: 2,
        attribute: 'CategoryName',
        strategy: 'Preferred Source',
        sourcePriority: 'ERP > Inventory',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 3,
        attribute: 'Description',
        strategy: 'Most Recent Update',
        sourcePriority: 'ERP > Inventory',
        fallback: 'Non-null',
      },
    ],

    matchRules: [
      { label: 'Exact Category Name', name: 'Rule1', type: 'Potential Match', scope: 'All' },
      { label: 'Fuzzy Category Description', name: 'Rule2', type: 'Potential Match', scope: 'All' },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Capitalize Category Name',
        appliesTo: 'CategoryName',
        providerType: 'String Normalization',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Trim Description',
        appliesTo: 'Description',
        providerType: 'String Clean-up',
        order: 2,
        status: 'Limited',
      },
    ],
  },

  Maintenance: {
    attributes: [
      {
        name: 'MaintenanceId',
        type: 'String',
        description: 'Unique identifier for each maintenance record.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'AssetId',
        type: 'String',
        description: 'Reference to the associated asset ID.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'MaintenanceDate',
        type: 'Date',
        description: 'Date when the maintenance activity was performed.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Technician',
        type: 'String',
        description: 'Name of the technician who performed the maintenance.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
    ],

    relationships: [{ from: 'Maintenance', relationship: 'belongsTo', to: 'Asset' }],

    survivorship: [
      {
        id: 1,
        attribute: 'MaintenanceId',
        strategy: 'Primary Key (Unique Identifier)',
        sourcePriority: '—',
        fallback: 'No Fallback (System Generated)',
      },
      {
        id: 2,
        attribute: 'AssetId',
        strategy: 'Foreign Key (Reference Integrity)',
        sourcePriority: '—',
        fallback: 'No Fallback (Linked from Asset)',
      },
      {
        id: 3,
        attribute: 'MaintenanceDate',
        strategy: 'Most Recent Update',
        sourcePriority: 'CMMS > ERP',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 4,
        attribute: 'Technician',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CMMS > HR',
        fallback: 'Non-null',
      },
    ],

    matchRules: [
      { label: 'Exact Maintenance Date & Asset', name: 'RuleA', type: 'Potential Match', scope: 'All' },
      { label: 'Technician Similarity Match', name: 'RuleB', type: 'Potential Match', scope: 'All' },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Validate Maintenance Date',
        appliesTo: 'MaintenanceDate',
        providerType: 'Date Validation',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Standardize Technician Name',
        appliesTo: 'Technician',
        providerType: 'String Normalization',
        order: 2,
        status: 'Limited',
      },
    ],
  },
};
